import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import useCustomPageTitle from '@platform-ui-common/hooks/useCustomPageTitle.js';
import Color, { BackgroundColors } from '@platform-ui-common/utilities/Color.js';
import { useApplicationProgress } from '@platform-ui/application/hooks/index.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import { isSectionComplete } from '@platform-ui/application/hooks/useApplicationProgress.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { getApplicantSections } from '@platform-ui/utilities/application/config.js';
import { SpecialSectionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { getQuestionRoute } from '@platform-ui/utilities/application/question.js';
import { getSectionRoute } from '@platform-ui/utilities/application/section.js';
import { Flex, Grid, Heading, Text } from '@radix-ui/themes';
import { FormEvent, useState } from 'react';
import { useHistory } from 'react-router';
import ApplicationQuestionGroupListReview from './components/ApplicationQuestionGroupListReview.js';
import ReviewSectionContainer from './components/ReviewSectionContainer.js';

export default function ApplicationReview(): JSX.Element {
  const application = useApplication();
  const config = useApplicationConfig();
  const sections = getApplicantSections(config);
  const { isComplete } = useApplicationProgress();
  const history = useHistory();
  const [showError, setShowError] = useState(false);
  const reviewSection = sections.find(({ key }) => key === SpecialSectionKeys.Review);
  const nextQuestion = getQuestionRoute(reviewSection.questionGroups[0].questions[0]);
  const incompleteSections = sections.filter(
    (section) =>
      section !== reviewSection && !isSectionComplete(section, application.answers, application),
  ).length;

  const pageTitle = 'Application Review';

  useCustomPageTitle(pageTitle);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isComplete) {
      history.push(makeRoute(nextQuestion, { applicationId: application.id }));
    } else {
      setShowError(true);
    }
  };

  return (
    <Flex direction="column" align="center" width="100%">
      <Flex direction="column" align="center" mb="5">
        <Heading size="7" weight="medium">
          {pageTitle}
        </Heading>
        <Text size="3" align="center">
          Please review the information you provided to make sure everything looks correct.
        </Text>
      </Flex>
      <Flex direction="column" align="center" asChild width="100%">
        <form onSubmit={handleSubmit} noValidate={true}>
          <Grid columns="1" rows="1" gap="3" width="100%" mx="auto" mb="5">
            {sections
              .filter((section) => section !== reviewSection)
              .map((section) => (
                <ReviewSectionContainer
                  key={section.key}
                  application={application}
                  title={section.name}
                  redirectRoute={getSectionRoute(section, application.answers)}
                  isComplete={isSectionComplete(section, application.answers, application) ?? false}
                >
                  <ApplicationQuestionGroupListReview
                    application={application}
                    applicationQuestionGroupList={section.questionGroups}
                    sectionTitle={section.name}
                  />
                </ReviewSectionContainer>
              ))}
          </Grid>
          <Flex>
            <div className="flex flex-col items-center gap-8">
              <ErrorDisplay
                message={`You have ${incompleteSections} incomplete ${
                  incompleteSections > 1 ? 'sections that are' : 'section that is'
                } required`}
                visible={!isComplete && showError}
              />
              <Button className={`${BackgroundColors[Color.Brand]}`} type="submit">
                Confirm
              </Button>
            </div>
          </Flex>
        </form>
      </Flex>
    </Flex>
  );
}
